import { CrewDropDownButton } from 'components/elements/crewDropDownButton/crewDropDownButton'
import { FC, memo, useCallback, useEffect, useState } from 'react'
import MoreHoriz from '~icons/material-symbols/more-horiz'
import { useAnnouncementContextMenu } from './useAnnouncementContextMenu'
import classNames from 'classnames'
import {
  AnnouncementContextMenuItems,
  AnnouncementContextMenuType,
} from 'enums/app'
import { useShowApiErrors } from 'hooks/useShowApiErrors'

const renderDropDownItem = (itemData: { name: string }) => (
  <span className="px-1.5 text-base whitespace-nowrap">{itemData.name}</span>
)

export type AnnouncementContextMenuProps = {
  announcementId: string | undefined // MenuType = ForAllの場合はundefined
  visible: boolean
  contextMenuType: AnnouncementContextMenuType
  showBorder: boolean
}

export const AnnouncementContextMenu: FC<AnnouncementContextMenuProps> = memo(
  (props) => {
    const [visibleContextMenu, setVisibleContextMenu] = useState<boolean>(false)
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false)

    const { contextMenuData, markAllRead, updateAnnouncementRead } =
      useAnnouncementContextMenu(props.contextMenuType)

    const [showApiErrors] = useShowApiErrors()

    // メニューの表示・非表示切り替え
    useEffect(() => {
      if (!showContextMenu) {
        // コンテキストメニューが非表示の時のみ、切り替えを受け付ける
        setVisibleContextMenu(props.visible)
      }
      // contextMenuの外側をクリックした際にprops.visibleを受け取りたいので全て依存に入れる
    }, [props.visible, showContextMenu])

    // ContextMenuが表示された時のイベントハンドラ
    const handleShowingItem = useCallback(() => {
      setShowContextMenu(true)
    }, [])

    // ContextMenuが非表示になった時のイベントハンドラ
    const handleHidingItem = useCallback(() => {
      setShowContextMenu(false)
    }, [])

    // ContextMenu内のitemクリック時のイベントハンドラ
    const handleClickItem = useCallback(
      (itemData: { id: string }) => {
        if (itemData.id === AnnouncementContextMenuItems.AllRead.action) {
          // 「すべて既読にする」のアクションを実行
          try {
            markAllRead()
          } catch (err) {
            showApiErrors(err)
          }
        } else if (
          itemData.id === AnnouncementContextMenuItems.MarkAsRead.action
        ) {
          // 「既読にする」のアクションを実行
          if (!props.announcementId) {
            // お知らせIDがない場合は何もしない
            return
          }

          try {
            updateAnnouncementRead(props.announcementId, true)
          } catch (err) {
            showApiErrors(err)
          }
        } else if (
          itemData.id === AnnouncementContextMenuItems.MarkAsUnread.action
        ) {
          // 「未読にする」のアクションを実行
          if (!props.announcementId) {
            // お知らせIDがない場合は何もしない
            return
          }

          try {
            updateAnnouncementRead(props.announcementId, false)
          } catch (err) {
            showApiErrors(err)
          }
        }
      },
      [markAllRead, props.announcementId, showApiErrors, updateAnnouncementRead]
    )

    return (
      <>
        {visibleContextMenu && (
          <div
            className={classNames(
              props.showBorder &&
                'crew-bg-default flex flex-row items-center border crew-border-gray gap-x-1'
            )}
          >
            <CrewDropDownButton
              icon={<MoreHoriz width={24} height={24} />}
              items={contextMenuData}
              keyExpr="id"
              displayExpr="name"
              showArrowIcon={false}
              showDivider={false}
              highlightSelected={false}
              stylingMode="text"
              buttonSize="sm"
              itemRender={renderDropDownItem}
              onShowing={handleShowingItem}
              onHiding={handleHidingItem}
              onItemClick={handleClickItem}
            />
          </div>
        )}
      </>
    )
  }
)
